import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gavel, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import clsx from 'clsx';

/**
 * Auction-house directory panel — collapsible section in the left sidebar.
 *
 * Lists every auction house we have stock for, with a lot count. Click a
 * house to filter the property list to that house. Sale dates are not yet
 * surfaced (the auctions.auction_date column is currently being populated
 * with scrape timestamps; once the scrapers are fixed to capture real sale
 * dates, this panel will show "Wed 7 May, London — 24 lots" entries instead
 * of just lot counts).
 */
export default function AuctionHousePanel({ onNavigate }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [houses, setHouses] = useState(null);
  const [error, setError] = useState(null);
  const [saleDatesAvailable, setSaleDatesAvailable] = useState(false);

  useEffect(() => {
    if (!open || houses) return;
    const ctrl = new AbortController();
    fetch('/api/auction-houses', { signal: ctrl.signal })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('Failed to load'))))
      .then(data => {
        setHouses(data.houses || []);
        setSaleDatesAvailable(!!data.sale_dates_available);
      })
      .catch(err => {
        if (err.name !== 'AbortError') setError('Could not load auction houses');
      });
    return () => ctrl.abort();
  }, [open, houses]);

  const handleHouseClick = (slug) => {
    navigate(`/properties?auction_house=${encodeURIComponent(slug)}`);
    if (onNavigate) onNavigate();
  };

  return (
    <div className="px-3 pb-3">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={clsx(
          'flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium transition-all',
          open
            ? 'bg-slate-800 text-slate-100'
            : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800',
        )}
      >
        <Gavel size={18} />
        <span className="flex-1 text-left">Filters</span>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>

      {open && (
        <div className="mt-2 ml-2 pl-2 border-l border-slate-800 space-y-0.5 max-h-72 overflow-y-auto">
          {!saleDatesAvailable && houses && houses.length > 0 && (
            <p className="text-[10px] text-amber-400/80 px-2 py-1.5 leading-tight">
              Sale dates coming soon — for now, click an auction house to see all of their lots in our system.
            </p>
          )}

          {error && <p className="text-xs text-red-400 px-2 py-1.5">{error}</p>}

          {!error && !houses && (
            <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-slate-500">
              <Loader2 size={12} className="animate-spin" /> Loading…
            </div>
          )}

          {houses && houses.length === 0 && !error && (
            <p className="text-xs text-slate-500 px-2 py-1.5">No auction stock right now.</p>
          )}

          {houses && houses.map((h) => (
            <button
              key={h.slug}
              type="button"
              onClick={() => handleHouseClick(h.slug)}
              className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-xs text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 transition-colors"
            >
              <span className="flex-1 text-left truncate">{h.name}</span>
              <span className="text-[10px] tabular-nums text-slate-500">{h.lots}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
